import { TableEvent } from '@baemingo/table-events';
import { LocalItem, LocalVariation } from '~Types';

/** Galaxy writes this clientId on table events created from QR "order now & pay later" */
export const GALAXY_CLIENT_ID = 'GALAXY';

/** Orders older than this when they print get a "delayed" note, so the kitchen knows why */
export const LATE_AFTER_MS = 5 * 60 * 1000;

/**
 * Longest outage we catch up on. Must stay well below PRINTED_TTL_MS: past the TTL the printed
 * ids are forgotten, so anything older could be reprinted after a reload.
 */
export const MAX_CATCH_UP_MS = 3 * 60 * 60 * 1000;

/** Rows of one order can arrive in different poll batches, so wait this long for stragglers */
export const BUFFER_MS = 1500;

/** Longest a bong waits for a removed variation's name before printing its id instead */
export const VARIATION_FETCH_TIMEOUT_MS = 3000;

export const PRINTED_STORAGE_KEY = 'qrPayLaterPrintedEventIds';

/**
 * When this device started printing pay later orders (ms). Orders created before it are history.
 * Kept across reloads so orders that arrive while the device is offline or reloading still print.
 */
export const WATCHING_SINCE_STORAGE_KEY = 'qrPayLaterWatchingSince';

/** How long a printed eventId is remembered. Far beyond MAX_CATCH_UP_MS, so a reload can't reprint */
export const PRINTED_TTL_MS = 24 * 60 * 60 * 1000;

type AddItemEvent = Extract<TableEvent, { type: 'ADD_ITEM' }>;

/**
 * An ADD_ITEM written by galaxy for a pay later order.
 * `clientId` and `tableId` exist at runtime (backend returns the full DynamoDB record)
 * but are missing from the `TableEvent` type.
 */
export type QrPayLaterItemEvent = AddItemEvent & {
  clientId: typeof GALAXY_CLIENT_ID;
  tableId?: string | null;
};

/** All rows of one pay later order: galaxy gives every event in an order the same createdAt */
export interface QrPayLaterOrder {
  key: string;
  chequeId: string;
  tableId: string | null;
  createdAt: string;
  events: QrPayLaterItemEvent[];
}

/** Menu data the mapping needs, injected so the mapping stays pure */
export interface MenuLookup {
  getCourseType: (itemId: string) => LocalItem['courseType'];
  getVariationName: (variationId: string) => string | null;
}

/** eventId -> time it was printed (ms) */
export type PrintedEventIds = Map<string, number>;

export const isQrPayLaterItemEvent = (event: TableEvent): event is QrPayLaterItemEvent =>
  event.type === 'ADD_ITEM' && (event as { clientId?: unknown }).clientId === GALAXY_CLIENT_ID;

/** True when the order is old enough that its ticket should say it's delayed */
export const isLate = (createdAt: string, nowMs: number): boolean => {
  const createdAtMs = Date.parse(createdAt);
  return Number.isFinite(createdAtMs) && nowMs - createdAtMs >= LATE_AFTER_MS;
};

/**
 * Splits unseen pay later events into the ones to print and the ones to skip (with reason).
 * Late events are still printed: an order that arrives after a network drop or reload must not
 * disappear. Only history (before watching started) and events past the catch-up window are skipped.
 */
export const partitionNewEvents = (
  events: QrPayLaterItemEvent[],
  nowMs: number,
  printed: PrintedEventIds,
  watchingSinceMs: number,
) => {
  const toPrint: QrPayLaterItemEvent[] = [];
  const alreadyPrinted: QrPayLaterItemEvent[] = [];
  const beforeWatching: QrPayLaterItemEvent[] = [];
  const tooOld: QrPayLaterItemEvent[] = [];

  events.forEach((event) => {
    const createdAtMs = Date.parse(event.createdAt);

    if (printed.has(event.eventId)) {
      alreadyPrinted.push(event);
    } else if (Number.isFinite(createdAtMs) && createdAtMs < watchingSinceMs) {
      beforeWatching.push(event);
    } else if (!Number.isFinite(createdAtMs) || nowMs - createdAtMs >= MAX_CATCH_UP_MS) {
      tooOld.push(event);
    } else {
      toPrint.push(event);
    }
  });

  return { toPrint, alreadyPrinted, beforeWatching, tooOld };
};

/** Parses the persisted watching-since time, null when missing or malformed */
export const parseWatchingSince = (raw: string | null): number | null => {
  if (!raw) {
    return null;
  }

  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
};

export const orderKey = (event: QrPayLaterItemEvent) => `${event.chequeId}|${event.createdAt}`;

/** Groups rows into orders, keeping the order in which they first appeared */
export const groupByOrder = (events: QrPayLaterItemEvent[]): QrPayLaterOrder[] => {
  const orders = new Map<string, QrPayLaterOrder>();

  events.forEach((event) => {
    const key = orderKey(event);
    const existing = orders.get(key);

    if (existing) {
      existing.events.push(event);
      existing.tableId = existing.tableId ?? event.tableId ?? null;
      return;
    }

    orders.set(key, {
      key,
      chequeId: event.chequeId,
      tableId: event.tableId ?? null,
      createdAt: event.createdAt,
      events: [event],
    });
  });

  return Array.from(orders.values());
};

export const toLocalItem = (event: QrPayLaterItemEvent, menu: MenuLookup): LocalItem => ({
  id: event.id,
  itemId: event.itemId,
  entryId: event.entryId,
  categoryId: event.categoryId,
  title: event.title,
  description: '',
  vatRate: event.vatRate,
  amount: event.amount,
  price: event.amount,
  discount: event.discount,
  count: 1,
  fraction: 1,
  variations: event.richVariations ?? [],
  // Galaxy stores the variation id as the name, so resolve the real name from the menu
  removedVariations: (event.removedVariations ?? []).map(
    (variation): LocalVariation => ({
      ...variation,
      name: menu.getVariationName(variation.id) ?? variation.name,
    }),
  ),
  courseType: menu.getCourseType(event.itemId),
});

/**
 * Parses the persisted printed ids, dropping expired and malformed entries.
 * Stored as [eventId, printedAt] pairs so untrusted keys never touch an object prototype.
 */
export const parsePrintedEventIds = (raw: string | null, nowMs: number): PrintedEventIds => {
  const printed: PrintedEventIds = new Map();

  if (!raw) {
    return printed;
  }

  let entries: unknown;
  try {
    entries = JSON.parse(raw);
  } catch {
    return printed;
  }

  if (!Array.isArray(entries)) {
    return printed;
  }

  entries.forEach((entry) => {
    if (!Array.isArray(entry)) {
      return;
    }

    const [eventId, printedAt] = entry;
    const isValid = typeof eventId === 'string' && typeof printedAt === 'number';

    if (isValid && nowMs - printedAt < PRINTED_TTL_MS) {
      printed.set(eventId, printedAt);
    }
  });

  return printed;
};

export const serializePrintedEventIds = (printed: PrintedEventIds): string =>
  JSON.stringify(Array.from(printed.entries()));
